/**
 * Pantalla de configuración. Es un asistente de tres pasos que se adapta a
 * dónde está corriendo la app:
 *
 *   self-hosted  →  formulario completo de conexión (tu PostgreSQL local).
 *   Vercel       →  la conexión llega por DATABASE_URL y NO es editable: el
 *                   sistema de archivos de una Function es de solo lectura, así
 *                   que no hay dónde guardar credenciales. Aquí el paso 1 pasa a
 *                   ser un diagnóstico de la conexión que ya existe.
 */
import { useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Cloud, Database, HardDrive, Info, ShieldCheck } from 'lucide-react'
import {
  ApiError,
  bootstrapAdmin,
  runMigrate,
  saveConnection,
  testConnection,
  type ProbeResponse,
} from '@/lib/api'
import { useSession } from '@/lib/session'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { cn } from '@/lib/utils'

const SSL_MODES = ['disable', 'prefer', 'require', 'verify-full'] as const

interface FormState {
  host: string
  port: string
  database: string
  schema: string
  user: string
  password: string
  ssl: (typeof SSL_MODES)[number]
  max: string
  connectionTimeoutMillis: string
}

const INITIAL: FormState = {
  host: '',
  port: '5432',
  database: '',
  schema: 'fleet',
  user: '',
  password: '',
  ssl: 'prefer',
  max: '10',
  connectionTimeoutMillis: '10000',
}

export function SetupPage() {
  const { status, refresh, loading } = useSession()

  const step: 1 | 2 | 3 = useMemo(() => {
    if (!status?.connected) return 1
    if (!status.schemaReady) return 2
    return 3
  }, [status])

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-2xl flex-col justify-center px-4 py-10">
      <header className="mb-6">
        <div className="mb-3 flex items-center gap-2.5">
          <span className="grid size-10 place-items-center rounded-xl bg-primary text-primary-foreground">
            <Database className="size-5" aria-hidden />
          </span>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Configurar GKDFleet</h1>
            <p className="text-sm text-muted-foreground">
              Tres pasos: conectar la base, crear el esquema y tu usuario administrador.
            </p>
          </div>
        </div>
        <Stepper current={step} />
      </header>

      {loading && !status ? (
        <Card>
          <CardBody className="text-sm text-muted-foreground">Comprobando el sistema…</CardBody>
        </Card>
      ) : (
        <div className="space-y-4">
          {step === 1 && <ConnectionStep onDone={refresh} />}
          {step === 2 && <MigrateStep onDone={refresh} />}
          {step === 3 && <AdminStep onDone={refresh} />}
        </div>
      )}
    </div>
  )
}

function Stepper({ current }: { current: 1 | 2 | 3 }) {
  const labels = ['Conexión', 'Esquema', 'Administrador']
  return (
    <ol className="flex items-center gap-2" aria-label="Progreso de configuración">
      {labels.map((label, i) => {
        const n = (i + 1) as 1 | 2 | 3
        const state = n < current ? 'done' : n === current ? 'active' : 'todo'
        return (
          <li key={label} className="flex flex-1 items-center gap-2">
            <span
              className={cn(
                'grid size-6 shrink-0 place-items-center rounded-full text-xs font-semibold',
                state === 'done' && 'bg-success text-success-foreground',
                state === 'active' && 'bg-primary text-primary-foreground',
                state === 'todo' && 'bg-secondary text-muted-foreground',
              )}
            >
              {state === 'done' ? <CheckCircle2 className="size-4" aria-hidden /> : n}
            </span>
            <span
              className={cn(
                'text-xs font-medium',
                state === 'todo' ? 'text-muted-foreground' : 'text-foreground',
              )}
            >
              {label}
            </span>
            {i < labels.length - 1 && <span className="h-px flex-1 bg-border" aria-hidden />}
          </li>
        )
      })}
    </ol>
  )
}

// ── Paso 1: conexión ────────────────────────────────────────────────────────

function ConnectionStep({ onDone }: { onDone: () => Promise<void> }) {
  const { status } = useSession()
  const [form, setForm] = useState<FormState>(INITIAL)
  const [fields, setFields] = useState<Record<string, string>>({})
  const [probe, setProbe] = useState<ProbeResponse | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState<'test' | 'save' | null>(null)

  const managedByEnv = status?.source === 'env' || !status?.editable

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
    setFields(({ [key]: _drop, ...rest }) => rest)
    setProbe(null)
  }

  async function submit(action: 'test' | 'save') {
    setBusy(action)
    setMessage(null)
    setFields({})
    try {
      const payload = {
        ...form,
        port: Number(form.port),
        max: Number(form.max),
        connectionTimeoutMillis: Number(form.connectionTimeoutMillis),
      }
      if (action === 'test') {
        setProbe(await testConnection(payload))
      } else {
        await saveConnection(payload)
        await onDone()
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setMessage(err.message)
        if (err.fields) setFields(err.fields)
      } else {
        setMessage(err instanceof Error ? err.message : 'Error desconocido')
      }
    } finally {
      setBusy(null)
    }
  }

  // ── Vercel: la conexión no se toca desde aquí ────────────────────────────
  if (managedByEnv) {
    return (
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Cloud className="size-4 text-primary" aria-hidden />
              Conexión gestionada por el entorno
            </span>
          }
        />
        <CardBody className="space-y-4">
          <Notice tone="info">
            Este despliegue lee la conexión de <code className="font-mono text-xs">DATABASE_URL</code>.
            No se puede cambiar desde la aplicación porque el sistema de archivos de una Function es
            de solo lectura. Edítala en <strong>Vercel → Settings → Environment Variables</strong> y
            vuelve a desplegar.
          </Notice>

          {status?.connected ? (
            <Notice tone="success">
              Conectado a {status.serverVersion ?? 'PostgreSQL'} en {status.latencyMs} ms.
            </Notice>
          ) : (
            <Notice tone="danger">
              {status?.error?.message ?? 'No se pudo conectar con la base de datos.'}
            </Notice>
          )}

          <Notice tone="warning">
            Con Neon en Vercel usa siempre la cadena que contiene{' '}
            <code className="font-mono text-xs">-pooler</code>. La cadena directa agota las
            conexiones del proyecto en cuanto hay concurrencia, y el fallo aparece tarde y de forma
            intermitente.
          </Notice>

          <Button type="button" variant="outline" onClick={() => void onDone()}>
            Volver a comprobar
          </Button>
        </CardBody>
      </Card>
    )
  }

  // ── Self-hosted: formulario completo ─────────────────────────────────────
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <HardDrive className="size-4 text-primary" aria-hidden />
            Conectar base de datos
          </span>
        }
      />
      <CardBody>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            void submit('save')
          }}
        >
          <p className="text-sm text-muted-foreground">
            Indica dónde vive tu PostgreSQL. Las credenciales se cifran y se guardan solo en este
            servidor: nunca llegan al navegador.
          </p>

          <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
            <Field
              label="Host"
              required
              autoComplete="off"
              placeholder="192.168.1.50"
              value={form.host}
              error={fields.host}
              onChange={(e) => set('host', e.target.value)}
            />
            <Field
              label="Puerto"
              required
              inputMode="numeric"
              value={form.port}
              error={fields.port}
              onChange={(e) => set('port', e.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Base de datos"
              required
              autoComplete="off"
              placeholder="gkdfleet"
              value={form.database}
              error={fields.database}
              onChange={(e) => set('database', e.target.value)}
            />
            <Field
              label="Esquema"
              value={form.schema}
              error={fields.schema}
              hint="Por defecto: fleet"
              onChange={(e) => set('schema', e.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Usuario"
              required
              autoComplete="off"
              placeholder="gkdfleet_app"
              value={form.user}
              error={fields.user}
              hint="Usa un rol restringido, no postgres"
              onChange={(e) => set('user', e.target.value)}
            />
            <Field
              label="Contraseña"
              required
              type="password"
              autoComplete="new-password"
              value={form.password}
              error={fields.password}
              onChange={(e) => set('password', e.target.value)}
            />
          </div>

          <fieldset>
            <legend className="mb-1.5 text-sm font-medium text-foreground">Modo SSL</legend>
            <div className="flex flex-wrap gap-2">
              {SSL_MODES.map((mode) => (
                <label
                  key={mode}
                  className={cn(
                    'flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm transition-colors lg:min-h-10',
                    form.ssl === mode
                      ? 'border-primary bg-primary-muted text-accent-foreground'
                      : 'border-input bg-card hover:bg-muted',
                  )}
                >
                  <input
                    type="radio"
                    name="ssl"
                    value={mode}
                    checked={form.ssl === mode}
                    onChange={() => set('ssl', mode)}
                    className="size-4 accent-[oklch(52%_0.17_258)]"
                  />
                  {mode}
                </label>
              ))}
            </div>
            {fields.ssl && (
              <p role="alert" className="mt-1.5 text-xs font-medium text-danger">
                {fields.ssl}
              </p>
            )}
          </fieldset>

          <details className="rounded-lg border border-border bg-muted/40 px-3 py-2">
            <summary className="cursor-pointer text-sm font-medium">Opciones avanzadas</summary>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <Field
                label="Pool máximo"
                inputMode="numeric"
                value={form.max}
                error={fields.max}
                onChange={(e) => set('max', e.target.value)}
              />
              <Field
                label="Timeout de conexión (ms)"
                inputMode="numeric"
                value={form.connectionTimeoutMillis}
                error={fields.connectionTimeoutMillis}
                onChange={(e) => set('connectionTimeoutMillis', e.target.value)}
              />
            </div>
          </details>

          {message && <Notice tone="danger">{message}</Notice>}

          {probe?.ok && (
            <Notice tone="success">
              Conectado · {probe.serverVersion} · {probe.latencyMs} ms · usuario{' '}
              <span className="font-mono text-xs">{probe.currentUser}</span>
            </Notice>
          )}
          {probe?.warnings.map((w) => (
            <Notice key={w} tone="warning">
              {w}
            </Notice>
          ))}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              loading={busy === 'test'}
              onClick={() => void submit('test')}
            >
              Probar conexión
            </Button>
            <Button type="submit" loading={busy === 'save'}>
              Guardar y continuar
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  )
}

// ── Paso 2: migraciones ─────────────────────────────────────────────────────

function MigrateStep({ onDone }: { onDone: () => Promise<void> }) {
  const { status } = useSession()
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const pending = status?.pendingMigrations ?? []
  const drifted = status?.driftedMigrations ?? []

  async function run() {
    setBusy(true)
    setMessage(null)
    try {
      const result = await runMigrate()
      setMessage(
        result.applied.length > 0
          ? `Aplicadas: ${result.applied.join(', ')}`
          : 'No había migraciones pendientes.',
      )
      await onDone()
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Database className="size-4 text-primary" aria-hidden />
            Crear el esquema
          </span>
        }
      />
      <CardBody className="space-y-4">
        {drifted.length > 0 && (
          <Notice tone="danger">
            Estas migraciones ya aplicadas cambiaron de contenido: {drifted.join(', ')}. El esquema
            real y el repositorio divergieron. Crea una migración nueva en vez de editar una
            existente.
          </Notice>
        )}

        {pending.length > 0 ? (
          <>
            <p className="text-sm text-muted-foreground">
              {pending.length} migración(es) pendiente(s). Cada una corre en su propia transacción y
              queda registrada con su checksum.
            </p>
            <ul className="space-y-1">
              {pending.map((name) => (
                <li key={name} className="flex items-center gap-2 text-sm">
                  <span className="size-1.5 rounded-full bg-warning" aria-hidden />
                  <span className="font-mono text-xs">{name}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <Notice tone="success">El esquema está al día.</Notice>
        )}

        {status?.platform === 'vercel' && (
          <Notice tone="info">
            En producción esto requiere <code className="font-mono text-xs">ALLOW_REMOTE_MIGRATE=1</code>{' '}
            en las variables de entorno. Actívalo para el despliegue inicial y vuelve a apagarlo
            después.
          </Notice>
        )}

        {message && <Notice tone="info">{message}</Notice>}

        <Button type="button" loading={busy} onClick={() => void run()} disabled={drifted.length > 0}>
          Ejecutar migraciones
        </Button>
      </CardBody>
    </Card>
  )
}

// ── Paso 3: administrador ───────────────────────────────────────────────────

function AdminStep({ onDone }: { onDone: () => Promise<void> }) {
  const [form, setForm] = useState({ fullName: '', email: '', password: '' })
  const [fields, setFields] = useState<Record<string, string>>({})
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit() {
    setBusy(true)
    setMessage(null)
    setFields({})
    try {
      await bootstrapAdmin(form)
      await onDone()
    } catch (err) {
      if (err instanceof ApiError) {
        setMessage(err.message)
        if (err.fields) setFields(err.fields)
      } else {
        setMessage(err instanceof Error ? err.message : 'Error desconocido')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <ShieldCheck className="size-4 text-primary" aria-hidden />
            Crear tu usuario administrador
          </span>
        }
      />
      <CardBody>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <p className="text-sm text-muted-foreground">
            Este formulario solo funciona mientras no exista ningún usuario, así que no queda abierto
            después.
          </p>

          <Field
            label="Nombre completo"
            required
            autoComplete="name"
            value={form.fullName}
            error={fields.fullName}
            onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
          />
          <Field
            label="Correo"
            required
            type="email"
            autoComplete="username"
            value={form.email}
            error={fields.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
          <Field
            label="Contraseña"
            required
            type="password"
            autoComplete="new-password"
            value={form.password}
            error={fields.password}
            hint="Mínimo 12 caracteres, con mayúsculas, minúsculas y números"
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          />

          {message && <Notice tone="danger">{message}</Notice>}

          <Button type="submit" loading={busy}>
            Crear administrador y entrar
          </Button>
        </form>
      </CardBody>
    </Card>
  )
}

// ── Avisos ──────────────────────────────────────────────────────────────────

const NOTICE_TONES = {
  info: { cls: 'bg-primary-muted text-accent-foreground', Icon: Info },
  success: { cls: 'bg-success-muted text-[oklch(40%_0.14_155)]', Icon: CheckCircle2 },
  warning: { cls: 'bg-warning-muted text-warning-foreground', Icon: AlertTriangle },
  danger: { cls: 'bg-danger-muted text-[oklch(40%_0.19_27)]', Icon: AlertTriangle },
} as const

function Notice({
  tone,
  children,
}: {
  tone: keyof typeof NOTICE_TONES
  children: React.ReactNode
}) {
  const { cls, Icon } = NOTICE_TONES[tone]
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex gap-2.5 rounded-lg px-3 py-2.5 text-sm', cls)}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="[&_code]:rounded [&_code]:bg-black/10 [&_code]:px-1 [&_code]:py-0.5">
        {children}
      </div>
    </div>
  )
}
